import React, { useState } from "react"
import {
  Modal,
  Form,
  Label,
  InputGroup,
  Input,
  Row,
  Col,
  Table,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import moment from "moment"
import { post } from "utils/apiClient"

const emptyRefRow = () => ({ type: "PO", value: "", pallets: "", cases: "", weight: "" })

const ScheduleModal = props => {
  const stop = props.stop || {}
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [submitted, setSubmitted] = useState(false)

  // One toggle for the optional changes; the current time is scheduled as-is unless it is opened.
  const [showChanges, setShowChanges] = useState(false)
  const [fromDate, setFromDate] = useState(null)
  const [fromTime, setFromTime] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [toTime, setToTime] = useState(null)
  const [refs, setRefs] = useState([emptyRefRow()])
  const [showRefs, setShowRefs] = useState(false)

  const reset = () => {
    setError(null)
    setSubmitted(false)
    setShowChanges(false)
    setFromDate(null)
    setFromTime(null)
    setToDate(null)
    setToTime(null)
    setRefs([emptyRefRow()])
    setShowRefs(false)
  }

  const close = () => {
    reset()
    props.toggle()
  }

  const done = () => {
    if (props.refreshTable) props.refreshTable()
    close()
  }

  const onDate = setter => dates => setter(dates && dates[0] ? moment(dates[0]).format("YYYY-MM-DD") : null)
  const onTime = setter => dates => setter(dates && dates[0] ? moment(dates[0]).format("HH:mm") : null)

  const addRow = () => setRefs(prev => [...prev, emptyRefRow()])
  const updateRow = (index, field, value) =>
    setRefs(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)))

  const buildStart = () => (fromDate && fromTime ? `${fromDate} ${fromTime}` : null)
  const buildEnd = () => (toDate && toTime ? `${toDate} ${toTime}` : null)

  const buildReferences = () =>
    refs
      .filter(r => r.value && r.value.trim() !== "")
      .map(r => {
        const out = { type: r.type, value: r.value.trim() }
        if (String(r.pallets).trim() !== "") out.pallets = Number(r.pallets)
        if (String(r.cases).trim() !== "") out.pieces = Number(r.cases)
        if (String(r.weight).trim() !== "") out.weightInLbs = Number(r.weight)
        return out
      })

  const currentRequested = stop.tmsRequestedAppointmentDate

  const submit = async e => {
    e.preventDefault()

    const start = buildStart()
    const end = buildEnd()
    // The time is optional: validate only when the user actually entered one.
    if (start || end) {
      if (!start || !end) {
        setError("Please choose both a start and end date/time, or leave both blank to keep the current time.")
        return
      }
      if (moment(start).isAfter(moment(end))) {
        setError("The start time must be before the end time.")
        return
      }
    }

    setError(null)
    setLoading(true)

    const references = buildReferences()
    const payload = { stopId: stop.stopId }
    if (start && end) payload.apptSchdRange = [{ start, end }]
    if (references.length) payload.references = references

    try {
      await post("crs/schedule", payload)
      setLoading(false)
      setSubmitted(true)
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.")
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={props.isOpen} toggle={close} centered size="lg">
      <div className="modal-header">
        <h5 className="modal-title mt-0">Schedule appointment</h5>
        <button type="button" onClick={close} className="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <Form onSubmit={submit}>
        <div className="modal-body">
          {submitted ? (
            <div className="text-center py-2">
              <i className="bx bx-check-circle text-primary" style={{ fontSize: 44 }} />
              <h5 className="text-primary mt-2 mb-1">Schedule request submitted</h5>
              <p className="text-muted mb-1">It may take a moment to appear in the timeline.</p>
            </div>
          ) : (
          <>
          <p className="mb-2">Send this stop to be scheduled with the details we already have.</p>
          {currentRequested && (
            <p className="mb-3">
              <strong>Current requested time:</strong> {currentRequested}
            </p>
          )}

          <div className="mb-2">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowChanges(!showChanges)}
            >
              {showChanges ? "− Keep current details" : "Change the date/time, or add reference numbers"}
            </button>
          </div>

          {showChanges && (
            <div className="mt-3">
              <h6 className="mb-1">New appointment time</h6>
              <p className="text-muted small mb-3">Leave blank to keep the current requested time.</p>
              <Row>
                <Col md="6" className="form-group mb-3">
                  <Label>From date</Label>
                  <InputGroup>
                    <Flatpickr
                      className="form-control d-block"
                      placeholder="dd M, yyyy"
                      options={{ altInput: true, altFormat: "F j, Y", dateFormat: "Y-m-d" }}
                      onChange={onDate(setFromDate)}
                    />
                  </InputGroup>
                </Col>
                <Col md="6" className="form-group mb-3">
                  <Label>From time</Label>
                  <InputGroup>
                    <Flatpickr
                      className="form-control d-block"
                      placeholder="Select time"
                      options={{ enableTime: true, noCalendar: true, dateFormat: "H:i" }}
                      onChange={onTime(setFromTime)}
                    />
                    <span className="input-group-text">
                      <i className="mdi mdi-clock-outline" />
                    </span>
                  </InputGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6" className="form-group mb-3">
                  <Label>To date</Label>
                  <InputGroup>
                    <Flatpickr
                      className="form-control d-block"
                      placeholder="dd M, yyyy"
                      options={{ altInput: true, altFormat: "F j, Y", dateFormat: "Y-m-d" }}
                      onChange={onDate(setToDate)}
                    />
                  </InputGroup>
                </Col>
                <Col md="6" className="form-group mb-3">
                  <Label>To time</Label>
                  <InputGroup>
                    <Flatpickr
                      className="form-control d-block"
                      placeholder="Select time"
                      options={{ enableTime: true, noCalendar: true, dateFormat: "H:i" }}
                      onChange={onTime(setToTime)}
                    />
                    <span className="input-group-text">
                      <i className="mdi mdi-clock-outline" />
                    </span>
                  </InputGroup>
                </Col>
              </Row>

              <div className="mt-1 mb-3">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowRefs(!showRefs)}
                >
                  {showRefs ? "− Hide Additional Reference Numbers" : "+ Add Additional Reference Numbers"}
                </button>
              </div>

              {showRefs && (
                <>
                  <Table className="table mb-2" size="sm">
                    <thead>
                      <tr>
                        <th style={{ width: "18%" }}>Type</th>
                        <th>Value</th>
                        <th style={{ width: "15%" }}>Pallets</th>
                        <th style={{ width: "15%" }}>Cases</th>
                        <th style={{ width: "15%" }}>Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refs.map((r, i) => (
                        <tr key={i}>
                          <td>
                            <Input
                              type="text"
                              placeholder="PO"
                              value={r.type}
                              onChange={e => updateRow(i, "type", e.target.value)}
                            />
                          </td>
                          <td>
                            <Input
                              type="text"
                              placeholder="Reference number(s)"
                              value={r.value}
                              onChange={e => updateRow(i, "value", e.target.value)}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              value={r.pallets}
                              onChange={e => updateRow(i, "pallets", e.target.value)}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              value={r.cases}
                              onChange={e => updateRow(i, "cases", e.target.value)}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              value={r.weight}
                              onChange={e => updateRow(i, "weight", e.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <button type="button" className="btn btn-link p-0" onClick={addRow}>
                    + Add another
                  </button>
                </>
              )}
            </div>
          )}

          {error && <div className="text-danger mt-3">{error}</div>}
          </>
          )}
        </div>

        <div className="modal-footer">
          {submitted ? (
            <button type="button" className="btn btn-secondary" onClick={done}>
              Close
            </button>
          ) : (
            <>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Scheduling..." : "Schedule"}
              </button>
              <button type="button" className="btn btn-secondary" onClick={close}>
                Close
              </button>
            </>
          )}
        </div>
      </Form>
    </Modal>
  )
}

export default ScheduleModal
